import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useArticleList } from "../../../api/articlesApi"
import { useCustomerList } from "../../../api/customersApi"
import { useProductDetail, useProductList } from "../../../api/productsApi"
import { useRegulations } from "../../../api/regulationsApi"
import { useSubstanceList } from "../../../api/substancesApi"
import { useSupplierList } from "../../../api/suppliersApi"
import {
  PRODUCT_STATUS_VALUES,
  singleSelectFacet,
  STATUS_LABEL_KEYS,
} from "../../../constants/masterDataFilters"
import { useFacetOptions } from "../../../hooks/useFacetOptions"
import {
  clearUrlParams,
  stringFilterParam,
  useArrayFilterParam,
} from "../../../hooks/useUrlFilterParams"
import { regulationFilterOptions } from "../../../utils/regulationLabel"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { RelBadge } from "../../shared/RelBadge"
import { EntityListPanel } from "../EntityListPanel"
import type { MasterDataSortValue } from "../MasterDataSort"
import { useRegisterMasterDataAgentContext } from "../masterDataAgentContext"
import { useDefaultRowSelection } from "../useDefaultRowSelection"
import { useNavSeedSearch } from "../useNavSeedSearch"

interface ProductListPanelProps {
  selectedIds: Set<string>
  onToggleSelected: (id: string) => void
}

export function ProductListPanel({
  selectedIds,
  onToggleSelected,
}: ProductListPanelProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get("id")
  useRegisterMasterDataAgentContext("product", selectedId)

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  const [sort, setSort] = useState<MasterDataSortValue>("name_asc")

  const [statusFilter, setStatusFilter] = useArrayFilterParam(
    searchParams,
    setSearchParams,
    "status",
  )
  const [customerFilter, setCustomerFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "customer_id",
  )
  const [regulationFilter, setRegulationFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "regulation_id",
  )
  const [supplierFilter, setSupplierFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "supplier_id",
  )
  const [articleFilter, setArticleFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "article_id",
  )
  const [substanceFilter, setSubstanceFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "substance_id",
  )
  const [noArticlesFilter, setNoArticlesFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "no_articles",
  )
  // Only the exact value "1" switches this filter on. Any other URL value is
  // treated as absent for BOTH the query and the facet, so the chip can never
  // show active while the list is unfiltered.
  const noArticlesActive = noArticlesFilter === "1" ? noArticlesFilter : null

  const customerFacet = useFacetOptions(useCustomerList, (c) => ({
    value: c.id,
    label: c.name,
  }))
  const supplierFacet = useFacetOptions(useSupplierList, (s) => ({
    value: s.id,
    label: s.name,
  }))
  const articleFacet = useFacetOptions(useArticleList, (a) => ({
    value: a.id,
    label: a.name,
  }))
  const substanceFacet = useFacetOptions(useSubstanceList, (s) => ({
    value: s.id,
    label: s.name,
  }))
  const { data: regulations } = useRegulations()

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useProductList({
    q: debouncedSearch || undefined,
    customer_id: customerFilter ?? undefined,
    supplier_id: supplierFilter ?? undefined,
    article_id: articleFilter ?? undefined,
    substance_id: substanceFilter ?? undefined,
    regulation_id: regulationFilter ?? undefined,
    status: statusFilter.length > 0 ? statusFilter.join(",") : undefined,
    no_articles: noArticlesActive ? true : undefined,
    sort,
    limit: 50,
  })

  const rows = data?.pages.flatMap((p) => p.items) ?? []

  useDefaultRowSelection(rows, selectedId, setSearchParams)

  const isNavRedirect = searchParams.get("source") === "nav"
  const { data: seedDetail, isError: seedError } = useProductDetail(
    isNavRedirect ? selectedId : null,
  )
  useNavSeedSearch({
    isNavRedirect,
    selectedId,
    detailLoaded: !!seedDetail,
    detailErrored: seedError,
    seedSearchValue: seedDetail
      ? (seedDetail.name ?? seedDetail.productNumber ?? "")
      : undefined,
    setSearch,
    setSearchParams,
  })

  function selectRow(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  return (
    <EntityListPanel
      agentTarget="products.list"
      filterTargetKey="products.filters"
      rows={rows}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onFetchMore={fetchNextPage}
      selectedId={selectedId}
      onSelectRow={selectRow}
      selectedIds={selectedIds}
      onToggleSelected={onToggleSelected}
      totalCount={data?.pages[0]?.total}
      sort={sort}
      onSortChange={setSort}
      search={search}
      onSearchChange={setSearch}
      searchEntityLabelKey="MC_MD_TAB_PRODUCTS"
      onClearAllFilters={() =>
        clearUrlParams(setSearchParams, [
          "customer_id",
          "supplier_id",
          "article_id",
          "substance_id",
          "regulation_id",
          "status",
          "no_articles",
        ])
      }
      facets={[
        {
          key: "customer",
          title: translate("MC_MD_FILTER_CUSTOMER_TITLE"),
          ...customerFacet,
          ...singleSelectFacet(customerFilter, setCustomerFilter),
        },
        {
          key: "supplier",
          title: translate("MC_MD_FILTER_SUPPLIER_TITLE"),
          ...supplierFacet,
          ...singleSelectFacet(supplierFilter, setSupplierFilter),
        },
        {
          key: "article",
          title: translate("MC_MD_FILTER_ARTICLE_TITLE"),
          ...articleFacet,
          ...singleSelectFacet(articleFilter, setArticleFilter),
        },
        {
          key: "substance",
          title: translate("MC_MD_FILTER_SUBSTANCE_TITLE"),
          ...substanceFacet,
          ...singleSelectFacet(substanceFilter, setSubstanceFilter),
        },
        {
          key: "framework",
          title: translate("MC_MD_FILTER_FRAMEWORK_TITLE"),
          options: regulationFilterOptions(regulations ?? [], translate),
          ...singleSelectFacet(regulationFilter, setRegulationFilter),
        },
        {
          key: "status",
          title: translate("MC_MD_FILTER_STATUS_TITLE"),
          options: PRODUCT_STATUS_VALUES.map((value) => ({
            value,
            label: translate(STATUS_LABEL_KEYS[value]),
          })),
          selected: statusFilter,
          onSelectionChange: setStatusFilter,
        },
        {
          key: "no_articles",
          title: translate("MC_MD_FILTER_NO_ARTICLES_TITLE"),
          options: [
            {
              value: "1",
              label: translate("MC_MD_FILTER_NO_ARTICLES_OPTION"),
            },
          ],
          ...singleSelectFacet(noArticlesActive, setNoArticlesFilter),
        },
      ]}
      renderContent={(row) => (
        <EntityListRowContent
          title={row.name}
          meta={
            <>
              <span className="font-mono">{row.productNumber}</span>
              {row.customerName && <span>{row.customerName}</span>}
            </>
          }
          badges={
            <>
              <RelBadge
                label={translate("MC_MD_REL_LABEL_ARTICLES")}
                labelOne={translate("MC_MD_REL_LABEL_ARTICLES_ONE")}
                count={row.articlesCount}
                tone="muted"
                items={row.relArticles.map((a) => ({
                  id: a.id,
                  primary: a.articleNumber,
                  secondary: a.name,
                }))}
                itemHref={(id) => `../articles?id=${id}&source=nav`}
                onShowAll={() => navigate(`../articles?product_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_SUPPLIERS")}
                labelOne={translate("MC_MD_REL_LABEL_SUPPLIERS_ONE")}
                count={row.supplierCount}
                tone="muted"
                items={row.relSuppliers.map((s) => ({
                  id: s.id,
                  primary: s.name,
                }))}
                itemHref={(id) => `../suppliers?id=${id}&source=nav`}
                onShowAll={() => navigate(`../suppliers?product_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_SUBSTANCES")}
                labelOne={translate("MC_MD_REL_LABEL_SUBSTANCES_ONE")}
                count={row.substancesCount}
                tone="muted"
                items={row.relSubstances.map((s) => ({
                  id: s.id,
                  primary: s.name,
                }))}
                itemHref={(id) => `../substances?id=${id}&source=nav`}
                onShowAll={() => navigate(`../substances?product_id=${row.id}`)}
              />
              <RelBadge
                label={translate("MC_MD_REL_LABEL_CUSTOMERS")}
                labelOne={translate("MC_MD_REL_LABEL_CUSTOMERS_ONE")}
                count={row.customersCount}
                tone="muted"
                items={row.relCustomers.map((c) => ({
                  id: c.id,
                  primary: c.name,
                }))}
                itemHref={(id) => `../customers?id=${id}&source=nav`}
                onShowAll={() => navigate(`../customers?product_id=${row.id}`)}
              />
            </>
          }
        />
      )}
    />
  )
}
